import { useEffect, useState } from "react";
import { api, auth } from "./api";

// Theme preference: 'light' | 'dark' | 'system' (default dark). Resolved theme lives on <html data-theme>.
// The inline script in public/index.html applies the same logic before first paint (no flash of wrong theme).
export const THEME_KEY = "tr_theme";
export const THEMES = ["light", "dark", "system"];
const EVT = "tr-theme";
// Light theme chrome: keep the sidebar in the dark palette ("light content, dark chrome") for contrast + brand presence.
export const LIGHT_DARK_CHROME = true;
let switchTimer;

export const getPreference = () => {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return THEMES.includes(v) ? v : "dark";
  } catch { return "dark"; }
};

export const systemTheme = () =>
  (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark");

export const resolveTheme = (pref = getPreference()) => (pref === "system" ? systemTheme() : pref);

export const currentTheme = () => document.documentElement.dataset.theme || resolveTheme();

export function applyTheme(theme, { animate = true } = {}) {
  const root = document.documentElement;
  if (root.dataset.theme !== theme) {
    if (animate) {
      root.classList.add("theme-switching");
      clearTimeout(switchTimer);
      switchTimer = setTimeout(() => root.classList.remove("theme-switching"), 220);
    }
    root.dataset.theme = theme;
  }
  const bg = getComputedStyle(root).getPropertyValue("--bg-app").trim();
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", bg);
  document.querySelector('link[rel="icon"][type="image/svg+xml"]')?.setAttribute("href", `/favicon-${theme}.svg`);
  window.dispatchEvent(new CustomEvent(EVT, { detail: { theme, preference: getPreference() } }));
}

// Manual choice: persist locally (all pages) + per account (logged in), then apply. Other tabs follow via `storage`.
export function setPreference(pref, { remote = true } = {}) {
  if (!THEMES.includes(pref)) return;
  try { localStorage.setItem(THEME_KEY, pref); } catch { /* private mode */ }
  applyTheme(resolveTheme(pref));
  if (remote && auth.getToken()) api.put("/ux/theme", { theme: pref }).catch(() => {});
}

// Server preference wins over the local fallback once we know it (called after /ux/prefs on Layout mount).
export function adoptServerPreference(pref) {
  if (THEMES.includes(pref) && pref !== getPreference()) {
    try { localStorage.setItem(THEME_KEY, pref); } catch { /* ignore */ }
    applyTheme(resolveTheme(pref));
  }
}

export function initTheme() {
  applyTheme(resolveTheme(), { animate: false });
  const mq = window.matchMedia?.("(prefers-color-scheme: light)");
  const onSystem = () => { if (getPreference() === "system") applyTheme(systemTheme()); };
  mq?.addEventListener?.("change", onSystem);
  const onStorage = (e) => { if (e.key === THEME_KEY) applyTheme(resolveTheme(getPreference())); };
  window.addEventListener("storage", onStorage);
  return () => { mq?.removeEventListener?.("change", onSystem); window.removeEventListener("storage", onStorage); };
}

// [preference, resolvedTheme] that re-renders on any theme change (this tab, other tabs, OS).
export function useTheme() {
  const [state, setState] = useState(() => ({ preference: getPreference(), theme: currentTheme() }));
  useEffect(() => {
    const h = (e) => setState({ preference: e?.detail?.preference ?? getPreference(), theme: e?.detail?.theme ?? currentTheme() });
    window.addEventListener(EVT, h);
    return () => window.removeEventListener(EVT, h);
  }, []);
  return state;
}
